import type { TranslateFn } from '../i18nFactory';

/**
 * Traduce un error desconocido a un mensaje que se le pueda mostrar a un usuario.
 *
 * El kit hacía `toast.error('Error', { description: err.message })`, así que al
 * usuario le llegaba el texto técnico de JavaScript en crudo — "Cannot read
 * properties of undefined", "Failed to fetch" — que además nunca está traducido.
 *
 * Orden de precedencia:
 *
 *  1. **Texto del backend.** Si el error trae `detail` / `message` / `title` de un
 *     `ProblemDetails`, gana: el backend recibe `Accept-Language` y lo devuelve ya
 *     traducido, y es más específico que cualquier cosa que el kit pueda inferir.
 *  2. **Código HTTP.** Sin texto del backend, el status se mapea a una clave del
 *     catálogo (403, 404, 409, 422, 5xx).
 *  3. **Fallo de red.** `TypeError: Failed to fetch` y equivalentes.
 *  4. **Genérico.** Cualquier otra cosa: mensaje genérico, y el texto técnico se
 *     devuelve aparte en `technical` para diagnóstico, nunca para la UI.
 *
 * Es una función pura y recibe `t`, para poder usarse desde hooks y desde módulos
 * sin acceso al contexto de React.
 *
 * ⚠️ El paso 1 tiene que reconocer TODAS las formas en que un adapter lanza, no
 * solo la de axios. `leerTextoBackend` nacía mirando únicamente las bolsas
 * `response.data` / `data` / `problem`, y con eso se le escapaban dos formas que
 * el propio kit produce y consume:
 *
 *   · `ApiError` del adapter por defecto, que deja el problem-details en `.body`
 *     (ver `adapter/createDefaultAdapter.ts`);
 *   · el error **plano**, que trae `status` + `code` + `detail` en la raíz porque
 *     el host ya normalizó la respuesta en su interceptor HTTP.
 *
 * En ambos casos el `detail` del backend existía y no se mostraba: el usuario veía
 * el mensaje por status ("Revisa los datos ingresados") o el genérico, y la causa
 * real —"Esta acción no está disponible para la clase de documento de esta
 * factura"— solo aparecía en la consola del navegador. `isConflictError` ya hacía
 * el duck-typing completo sobre esas mismas formas; este módulo tenía que ser
 * simétrico con él, no un subconjunto.
 *
 * @see src/components/MasterDocument/smart/conflictError.ts - mismo duck-typing sobre las formas de lanzar
 * @see src/components/MasterDocument/smart/crossingFetch.ts - mismo criterio para los errores de cruce
 */

/** Error ya listo para mostrar. */
export interface ClassifiedError {
  /** Texto para el usuario. Siempre presente y siempre en el idioma activo. */
  message: string;
  /** Clave usada, o `undefined` si el texto lo puso el backend. */
  messageKey?: string;
  /** Texto técnico original. Para `console.error`, jamás para la UI. */
  technical?: string;
}

const CLAVE_POR_STATUS: Record<number, string> = {
  400: 'master_document.error.validation',
  401: 'master_document.error.unauthorized',
  403: 'master_document.error.forbidden',
  404: 'master_document.error.notFound',
  409: 'master_document.error.conflict',
  422: 'master_document.error.validation',
};

/** Extrae el status HTTP de las formas habituales (fetch, axios, ProblemDetails). */
function leerStatus(e: Record<string, unknown>): number | undefined {
  const candidatos = [
    e.status,
    e.statusCode,
    (e.response as Record<string, unknown> | undefined)?.status,
    (e.data as Record<string, unknown> | undefined)?.status,
  ];
  for (const c of candidatos) {
    const n = typeof c === 'string' ? Number(c) : c;
    if (typeof n === 'number' && Number.isFinite(n) && n >= 100) return n;
  }
  return undefined;
}

/**
 * ¿Es un texto que se le puede mostrar tal cual a un usuario?
 *
 * El `title` de un problem-details del ERP Comercial NO es una frase: es la CLAVE
 * i18n de la excepción (`error.capability_not_supported`). El `detail` es el texto
 * ya traducido. Como `title` sigue siendo el último recurso de la cadena, se
 * descartan las cadenas con forma de clave (segmentos en minúscula unidos por
 * puntos y sin espacios) para no pintarle una clave cruda al usuario.
 */
function esTextoParaUsuario(valor: unknown): valor is string {
  if (typeof valor !== 'string' || valor.trim() === '') return false;
  return !/^[a-z0-9_]+(\.[a-z0-9_]+)+$/.test(valor.trim());
}

/** Extrae el texto que el backend haya enviado, si lo hay. */
function leerTextoBackend(e: Record<string, unknown>): string | undefined {
  const bolsas = [
    (e.response as Record<string, unknown> | undefined)?.data,
    e.data,
    // `ApiError` del adapter por defecto del kit: el problem-details va en `.body`.
    e.body,
    e.problem,
    // Host que normaliza en su interceptor y conserva el error de axios original.
    ((e.originalError as Record<string, unknown> | undefined)?.response as
      | Record<string, unknown>
      | undefined)?.data,
  ].filter((b) => b != null && typeof b === 'object') as Array<Record<string, unknown>>;

  for (const bolsa of bolsas) {
    const texto = bolsa.detail ?? bolsa.message ?? bolsa.title;
    if (esTextoParaUsuario(texto)) return texto;
  }

  // Forma PLANA: el host ya aplanó el problem-details sobre el error mismo. Se leen
  // solo los campos que son exclusivos de un problem-details (`detail` / `title`);
  // `message` queda fuera a propósito, porque en un `Error` corriente es el texto
  // técnico de JavaScript y ése nunca se le muestra al usuario.
  const plano = e.detail ?? e.title;
  return esTextoParaUsuario(plano) ? plano : undefined;
}

/** Heurística de fallo de red: `fetch` lanza `TypeError` sin status. */
function esFalloDeRed(e: Record<string, unknown>, status: number | undefined): boolean {
  if (status !== undefined) return false;
  const nombre = typeof e.name === 'string' ? e.name : '';
  const mensaje = typeof e.message === 'string' ? e.message.toLowerCase() : '';
  return (
    nombre === 'TypeError'
    && (mensaje.includes('fetch') || mensaje.includes('network') || mensaje.includes('load failed'))
  );
}

/**
 * Clasifica un error para mostrarlo al usuario.
 *
 * @example
 * ```ts
 * const { message, technical } = classifyError(err, t);
 * if (technical) console.error('[MasterDocument]', technical);
 * toast.error(t('master_document.chrome.error'), { description: message });
 * ```
 */
export function classifyError(err: unknown, t: TranslateFn): ClassifiedError {
  const generico = (technical?: string): ClassifiedError => ({
    message: t('master_document.error.unexpected'),
    messageKey: 'master_document.error.unexpected',
    technical,
  });

  if (err == null) return generico();

  if (typeof err === 'string') {
    // Una cadena suelta suele ser un mensaje ya pensado para el usuario.
    return err.trim() === '' ? generico() : { message: err };
  }

  if (typeof err !== 'object') return generico(String(err));

  const e = err as Record<string, unknown>;
  const technical = typeof e.message === 'string' ? e.message : undefined;

  const backend = leerTextoBackend(e);
  if (backend) return { message: backend, technical };

  const status = leerStatus(e);
  if (status !== undefined) {
    const clave =
      CLAVE_POR_STATUS[status] ?? (status >= 500 ? 'master_document.error.serverUnavailable' : undefined);
    if (clave) return { message: t(clave), messageKey: clave, technical };
  }

  if (esFalloDeRed(e, status)) {
    return {
      message: t('master_document.error.network'),
      messageKey: 'master_document.error.network',
      technical,
    };
  }

  return generico(technical);
}
